import { useEffect, useRef, useState } from 'react'
import useScript from '@web/components/utils/useScript'
import { Input } from '@web/components/atoms/Input'
import { AddressInput } from '@web/graphql-types'
// import { FormHelperText } from '@web/components/atoms/Form'
import { Button } from '@web/components/atoms/Button'
import { Field } from '@web/components/atoms/Field'

type GoogleAutocompleteProps = {
  apiKey: string
  initialValue: string
  address: AddressInput | undefined | null
  isManual: boolean
  isRequired?: boolean
  onChange: (fullAddress: string, address: AddressInput) => void
  onSwitchToManualEntry: () => void
}

const GoogleAutocomplete = ({
  apiKey,
  address,
  initialValue,
  isManual,
  isRequired,
  onChange,
  onSwitchToManualEntry,
}: GoogleAutocompleteProps) => {
  const googleMapsScriptUrl = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`
  const scriptReady = useScript(googleMapsScriptUrl)
  const inputRef = useRef<HTMLInputElement>(null)
  const [value, setValue] = useState(initialValue)
  const [isMaybeIncomplete, setIsMaybeIncomplete] = useState(false)
  const [hasGoogleVerified, setHasGoogleVerified] = useState(false)

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  useEffect(() => {
    if (hasGoogleVerified) {
      setIsMaybeIncomplete(false)
    }
  }, [hasGoogleVerified])

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore google is defined
    if (!scriptReady || !window.google || !inputRef.current) {
      return
    }
    // Default location (central Australia, near Alice Springs)
    const defaultLocation = { lat: -25.2744, lng: 133.7751 }
    const defaultBounds = {
      north: defaultLocation.lat + 5, // Larger area to encompass most of Australia
      south: defaultLocation.lat - 5,
      east: defaultLocation.lng + 5,
      west: defaultLocation.lng - 5,
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore google is defined
    const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
      types: ['address'],
      bounds: defaultBounds,
      componentRestrictions: { country: 'au' },
      fields: ['address_components', 'formatted_address'],
    })

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace()
      setValue(place.formatted_address ?? '')
      setHasGoogleVerified(true)
      const newAddress: AddressInput = {
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        postcode: '',
        country: '',
      }

      for (const component of place.address_components ?? []) {
        const componentType = component.types[0]
        switch (componentType) {
          case 'subpremise':
            newAddress.addressLine1 = `${component.long_name}/`
            break
          case 'street_number':
            newAddress.addressLine1 += `${component.long_name} `
            break
          case 'route':
            newAddress.addressLine1 += component.long_name
            break
          case 'locality':
            newAddress.city = component.long_name
            break
          case 'administrative_area_level_1':
            newAddress.state = component.short_name
            break
          case 'postal_code':
            newAddress.postcode = component.long_name
            break
          case 'country':
            newAddress.country = component.long_name
            break
          default:
            break
        }
      }

      onChange(place.formatted_address ?? '', newAddress)
    })
  }, [scriptReady])

  return (
    <>
      <input
        type="text"
        style={{ position: 'absolute', top: '-9999px', left: '-9999px' }}
        autoComplete="google-only" // Hidden input to prevent Chrome autofill
        aria-autocomplete="none"
      />
      <Field
        label="Address"
        required={isRequired}
        invalid={isMaybeIncomplete}
        errorText={
          <>
            The address seems incomplete.{' '}
            <Button
              buttonAppearance="unstyled"
              variant="plain"
              fontSize="0.875rem"
              onClick={() => {
                setIsMaybeIncomplete(false)
                onChange(value, {})
              }}
            >
              Continue anyway.
            </Button>
          </>
        }
        helperText={
          <>
            Can&rsquo;t find your address?{' '}
            <Button buttonAppearance="unstyled" variant="plain" fontSize="0.875rem" onClick={onSwitchToManualEntry}>
              Enter manually
            </Button>
          </>
        }
        helperTextColor="inherit"
      >
        <Input
          id="google-autocomplete-field"
          aria-autocomplete="none"
          autoComplete="google-only"
          ref={inputRef}
          bgColor="ge.white"
          color="#000000"
          placeholder="Enter an address"
          onChange={(e) => {
            setHasGoogleVerified(false)
            setIsMaybeIncomplete(false)

            if (value === '' && e.currentTarget.value.length > 3) {
              setIsMaybeIncomplete(true)
            }

            setValue(e.currentTarget.value)
          }}
          onBlur={() => {
            if (!hasGoogleVerified && !isManual && value) {
              setIsMaybeIncomplete(true)
            }
          }}
          required={isRequired}
          value={value}
        />
      </Field>
    </>
  )
}

export default GoogleAutocomplete
